import type { StaticCompositionRenderPlan } from './planning';
import { StaticExportError } from './model';

export interface StaticWebGlCapabilities {
  readonly maxRenderbufferSize: number;
  readonly maxTextureSize: number;
  readonly maxViewportWidth: number;
  readonly maxViewportHeight: number;
  readonly maxMapWidth: number;
  readonly maxMapHeight: number;
}

let cachedCapabilities: StaticWebGlCapabilities | null | undefined;

function positiveInteger(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? Math.floor(value)
    : null;
}

/** Queries a tiny context once; no export-sized buffer is allocated during preflight. */
export function getStaticWebGlCapabilities(): StaticWebGlCapabilities | null {
  if (cachedCapabilities !== undefined) return cachedCapabilities;
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  const gl = canvas.getContext('webgl2') ?? canvas.getContext('webgl');
  if (!gl) {
    cachedCapabilities = null;
    return cachedCapabilities;
  }

  const maxRenderbufferSize = positiveInteger(gl.getParameter(gl.MAX_RENDERBUFFER_SIZE));
  const maxTextureSize = positiveInteger(gl.getParameter(gl.MAX_TEXTURE_SIZE));
  const viewport = gl.getParameter(gl.MAX_VIEWPORT_DIMS) as Int32Array | number[];
  const maxViewportWidth = positiveInteger(viewport?.[0]);
  const maxViewportHeight = positiveInteger(viewport?.[1]);
  const loseContext = gl.getExtension('WEBGL_lose_context');
  loseContext?.loseContext();

  if (
    maxRenderbufferSize === null ||
    maxTextureSize === null ||
    maxViewportWidth === null ||
    maxViewportHeight === null
  ) {
    cachedCapabilities = null;
    return cachedCapabilities;
  }

  cachedCapabilities = {
    maxRenderbufferSize,
    maxTextureSize,
    maxViewportWidth,
    maxViewportHeight,
    maxMapWidth: Math.min(maxRenderbufferSize, maxTextureSize, maxViewportWidth),
    maxMapHeight: Math.min(maxRenderbufferSize, maxTextureSize, maxViewportHeight),
  };
  return cachedCapabilities;
}

export function assertStaticMapFrameSupported(
  plan: StaticCompositionRenderPlan,
  capabilities: StaticWebGlCapabilities | null,
): asserts capabilities is StaticWebGlCapabilities {
  const { widthPx, heightPx } = plan.mapFrame.backingSize;
  if (
    capabilities === null ||
    widthPx > capabilities.maxMapWidth ||
    heightPx > capabilities.maxMapHeight
  ) {
    throw new StaticExportError(
      'webgl-limit',
      `${plan.dpi} DPI is too large for this map frame on this device. Choose a lower resolution.`,
    );
  }
}

export function resetStaticWebGlCapabilitiesForTests(): void {
  cachedCapabilities = undefined;
}
